import { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  createManualAppointment,
  SERVICE_TYPES,
  APPOINTMENT_TYPES,
} from '@/lib/appointments-service';
import { Loader2, Plus } from 'lucide-react';

interface CreateAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAppointmentCreated: () => void;
}

const CreateAppointmentDialog = ({
  open,
  onOpenChange,
  onAppointmentCreated,
}: CreateAppointmentDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    service: '',
    appointment_type: 'In-person',
    appointment_date: '',
    appointment_time: '',
    language: 'en',
    situation: '',
    createCalendarEvent: true,
    sendConfirmationEmail: true,
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.email || !formData.phone || !formData.date_of_birth || !formData.service) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Calculate appointment start and end times
      let appointmentStart: string | undefined;
      let appointmentEnd: string | undefined;
      
      if (formData.appointment_date && formData.appointment_time) {
        const dateTime = new Date(`${formData.appointment_date}T${formData.appointment_time}`);
        appointmentStart = dateTime.toISOString();
        appointmentEnd = new Date(dateTime.getTime() + 60 * 60 * 1000).toISOString(); // 1 hour duration
      }

      // Create the appointment
      const appointment = await createManualAppointment({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        date_of_birth: formData.date_of_birth,
        service: formData.service,
        situation: formData.situation || 'Manual booking created by admin',
        appointment_type: formData.appointment_type,
        appointment_start: appointmentStart,
        appointment_end: appointmentEnd,
        language: formData.language,
        payment_status: 'confirmed',
      });

      // Create Google Calendar event if requested
      if (formData.createCalendarEvent && appointmentStart) {
        try {
          let description = `Client: ${formData.full_name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nService: ${formData.service}\n\nSituation:\n${formData.situation || 'Manual booking'}`;
          
          if (formData.appointment_type === 'In-person') {
            description += '\n\n📍 In-Person Appointment at:\n10925 Beechnut St A106, Houston, TX 77072';
          } else if (formData.appointment_type === 'Phone') {
            description += '\n\n📞 Phone Consultation - Client will be called';
          }

          const { data, error } = await supabase.functions.invoke('create-calendar-event', {
            body: {
              summary: `${formData.appointment_type === 'Video' ? '🎥' : formData.appointment_type === 'Phone' ? '📞' : '🏢'} Legal Consultation - ${formData.full_name}`,
              description,
              start_time: appointmentStart,
              end_time: appointmentEnd,
              timezone: 'America/Chicago',
              attendee_email: formData.email,
              attendee_name: formData.full_name,
            },
          });

          if (!error && data?.event_id) {
            // Update appointment with calendar event ID
            await supabase
              .from('bookings')
              .update({ calendar_event_id: data.event_id })
              .eq('id', appointment.id);
          }
        } catch (calendarError) {
          console.error('Error creating calendar event:', calendarError);
          // Don't fail the whole operation if calendar fails
        }
      }

      // Send confirmation emails if requested
      if (formData.sendConfirmationEmail) {
        try {
          await supabase.functions.invoke('send-booking-confirmation', {
            body: {
              bookingId: appointment.booking_id,
              email: formData.email,
              fullName: formData.full_name,
              service: formData.service,
              appointmentType: formData.appointment_type,
              appointmentStart,
              paymentMethod: 'Manual Entry',
              language: formData.language,
            },
          });

          await supabase.functions.invoke('notify-team-booking', {
            body: {
              bookingId: appointment.booking_id,
              fullName: formData.full_name,
              email: formData.email,
              phone: formData.phone,
              service: formData.service,
              appointmentType: formData.appointment_type,
              appointmentStart,
              situation: formData.situation || 'Manual booking created by admin',
              paymentMethod: 'Manual Entry',
              language: formData.language,
            },
          });
        } catch (emailError) {
          console.error('Error sending confirmation emails:', emailError);
          // Don't fail the whole operation if email fails
        }
      }

      toast({
        title: 'Appointment Created',
        description: `New appointment for ${formData.full_name} has been created`,
      });

      // Reset form and close dialog
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        service: '',
        appointment_type: 'In-person',
        appointment_date: '',
        appointment_time: '',
        language: 'en',
        situation: '',
        createCalendarEvent: true,
        sendConfirmationEmail: true,
      });
      onOpenChange(false);
      onAppointmentCreated();
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to create appointment',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Appointment
          </DialogTitle>
          <DialogDescription>
            Manually create a new appointment for a client
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information */}
          <div className="space-y-4">
            <h3 className="font-semibold">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth *</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="language">Preferred Language</Label>
                <Select value={formData.language} onValueChange={(value) => handleInputChange('language', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="vi">Tiếng Việt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Appointment Details */}
          <div className="space-y-4">
            <h3 className="font-semibold">Appointment Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service">Service *</Label>
                <Select value={formData.service} onValueChange={(value) => handleInputChange('service', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment_type">Appointment Type</Label>
                <Select value={formData.appointment_type} onValueChange={(value) => handleInputChange('appointment_type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment_date">Date</Label>
                <Input
                  id="appointment_date"
                  type="date"
                  value={formData.appointment_date}
                  onChange={(e) => handleInputChange('appointment_date', e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment_time">Time</Label>
                <Input
                  id="appointment_time"
                  type="time"
                  value={formData.appointment_time}
                  onChange={(e) => handleInputChange('appointment_time', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Situation */}
          <div className="space-y-2">
            <Label htmlFor="situation">Situation / Notes</Label>
            <Textarea
              id="situation"
              value={formData.situation}
              onChange={(e) => handleInputChange('situation', e.target.value)}
              placeholder="Brief description of the client's legal situation..."
              rows={4}
            />
          </div>

          {/* Options */}
          <div className="space-y-4">
            <h3 className="font-semibold">Options</h3>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.createCalendarEvent}
                  onChange={(e) => handleInputChange('createCalendarEvent', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Create Google Calendar event</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sendConfirmationEmail}
                  onChange={(e) => handleInputChange('sendConfirmationEmail', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Send confirmation emails</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Appointment
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAppointmentDialog;
